import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { COLORS } from "@/lib/products";
import { PRODUCT } from "@/constants/testIds";
import { Minus, Plus, X, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProductDialog({ product, open, onOpenChange }) {
  const [color, setColor] = useState(COLORS[0].key);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (open) {
      setColor(COLORS[0].key);
      setQty(1);
    }
  }, [open, product?.id]);

  if (!product) return null;

  const activeColor = COLORS.find((c) => c.key === color);

  const buildMailto = () => {
    const subject = `Παραγγελία — ${product.title} (${product.tag})`;
    const bodyLines = [
      `Γεια σας,`,
      ``,
      `Θα ήθελα να παραγγείλω το παρακάτω από το blouzes4all.gr:`,
      ``,
      `• Σχέδιο: ${product.title}`,
      `• Κωδικός: ${product.id}`,
      `• Χρώμα: ${activeColor?.name}`,
      `• Ποσότητα: ${qty}`,
      `• Τιμή ανά τμχ.: ${product.price}`,
      ``,
      `Στοιχεία μου (συμπληρώστε):`,
      `Ονοματεπώνυμο: `,
      `Διεύθυνση: `,
      `Τηλέφωνο: `,
      `Μέγεθος: (S / M / L / XL)`,
      ``,
      `Ευχαριστώ!`,
    ];
    const body = bodyLines.join("\n");
    return `mailto:medhealthgr@gmail.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={PRODUCT.dialog}
        className="max-w-5xl w-[95vw] p-0 border border-[var(--ink)] rounded-none bg-[var(--bg)] gap-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">{product.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {product.desc} Επιλογές: χρώμα και ποσότητα. Παραγγελία μέσω email.
        </DialogDescription>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 h-full"
            >
              {/* Image */}
              <div className="relative border-b md:border-b-0 md:border-r border-[var(--ink)] bg-[var(--bg-2)]">
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-40"
                  style={{
                    background: `radial-gradient(circle at 50% 40%, ${activeColor?.hex}55, transparent 60%)`,
                  }}
                />
                <motion.img
                  key={product.image}
                  initial={{ scale: 1.05, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  src={product.image}
                  alt={`Σχέδιο μπλουζάκι ${product.title} — προεπισκόπηση`}
                  className="relative w-full h-full object-cover aspect-square md:aspect-auto md:min-h-[70vh]"
                />
                <div className="absolute top-4 left-4 font-mono-caps text-[10px] bg-[var(--bg)] border border-[var(--ink)] px-2 py-1">
                  {product.tag} · {product.id.toUpperCase()}
                </div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-[var(--bg)] border border-[var(--ink)] px-3 py-2">
                  <span
                    aria-hidden
                    className="w-4 h-4 rounded-full border border-[var(--ink)]"
                    style={{ background: activeColor?.hex }}
                  />
                  <span className="font-mono-caps text-[10px] text-[var(--ink)]">
                    ΕΠΙΛΟΓΗ: {activeColor?.name}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="relative p-6 md:p-10 flex flex-col">
                <button
                  data-testid={PRODUCT.closeBtn}
                  onClick={() => onOpenChange(false)}
                  className="absolute top-4 right-4 border border-[var(--ink)] p-2 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
                  aria-label="Κλείσιμο"
                >
                  <X className="w-4 h-4" />
                </button>

                <span className="font-mono-caps text-[10px] mb-4">
                  BLOUZES4ALL · Νο. {product.id.split("-")[1]}
                </span>
                <h2
                  data-testid={PRODUCT.title}
                  className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-6"
                >
                  {product.title}
                </h2>
                <p className="text-base md:text-lg text-[var(--ink-70)] leading-relaxed mb-8 max-w-md">
                  {product.desc}
                </p>

                {/* Color picker */}
                <div className="mb-8">
                  <div className="font-mono-caps text-[10px] mb-3">
                    ΧΡΩΜΑ — <span className="text-[var(--pink)]">{activeColor?.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {COLORS.map((c) => (
                      <button
                        key={c.key}
                        data-testid={`${PRODUCT.colorSwatchPrefix}-${c.key}`}
                        onClick={() => setColor(c.key)}
                        aria-label={c.name}
                        data-active={color === c.key}
                        className="swatch"
                        style={{ background: c.hex }}
                      />
                    ))}
                  </div>
                </div>

                {/* Quantity */}
                <div className="mb-10">
                  <div className="font-mono-caps text-[10px] mb-3">ΠΟΣΟΤΗΤΑ</div>
                  <div className="inline-flex items-stretch border border-[var(--ink)]">
                    <button
                      data-testid={PRODUCT.qtyDec}
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="px-4 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
                      aria-label="Μείωση"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <div
                      data-testid={PRODUCT.qtyValue}
                      className="w-14 flex items-center justify-center font-display text-2xl border-x border-[var(--ink)]"
                    >
                      {qty}
                    </div>
                    <button
                      data-testid={PRODUCT.qtyInc}
                      onClick={() => setQty((q) => Math.min(99, q + 1))}
                      className="px-4 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
                      aria-label="Αύξηση"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Order */}
                <div className="mt-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    data-testid={PRODUCT.orderBtn}
                    href={buildMailto()}
                    className="btn-lime"
                  >
                    ΠΑΡΑΓΓΕΛΙΑ ΜΕΣΩ EMAIL <Send className="w-4 h-4" />
                  </a>
                  <div className="font-mono-caps text-[10px] leading-relaxed">
                    ΣΥΝΟΛΟ ΕΝΔΕΙΚΤΙΚΟ<br />
                    <span className="font-display text-xl text-[var(--ink)]">
                      {(parseFloat(product.price) * qty).toFixed(0)}€
                    </span>
                  </div>
                </div>
                <p className="mt-6 text-[11px] text-[var(--ink-muted)] max-w-sm">
                  Πατώντας “Παραγγελία μέσω Email” ανοίγει το mail σας με
                  προσυμπληρωμένο το αίτημα. Απαντάμε συνήθως εντός 24 ωρών.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
